/**
 * @CODE:ACC-003-NAVER-MAPS-001
 * Naver Maps SDK Loader Utility
 *
 * Utility for dynamically loading Naver Maps V3 API:
 * - Prevents multiple script loading
 * - Caches loading promise for concurrent requests
 * - Handles loading and error states
 * - Type-safe SDK access
 *
 * Reference: Naver Maps V3 API Documentation
 * https://navermaps.github.io/maps.js.ncp/docs/naver.maps.Service.html
 */

/**
 * Global naver object with maps API
 */
declare global {
  interface Window {
    naver?: {
      maps: {
        Service: any;
        Map: any;
        Marker: any;
        LatLng: any;
        LatLngBounds: any;
        Event: any;
        Polyline: any;
        Point: any;
        Position: any;
        MapTypeControlStyle: any;
        ZoomControlStyle: any;
      };
    };
  }
}

/** Flag to track if Naver Maps is currently being loaded */
let isLoading = false;

/** Flag to track if Naver Maps has already been loaded */
let isLoaded = false;

/** Promise for ongoing load operation */
let loadPromise: Promise<any> | null = null;

/**
 * Load Naver Maps SDK asynchronously (New API)
 * Prevents multiple script tags and handles concurrent requests
 * Uses new oapi.map.naver.com endpoint with callback pattern
 *
 * @param clientId - Naver Maps API Client ID (ncpKeyId for new API)
 * @param submodules - Optional comma-separated submodules (e.g., 'geocoder,drawing')
 * @param lang - Map language (ko, en, ja, zh) - default: 'ko'
 * @returns Promise resolving to window.naver object
 * @throws Error if SDK fails to load
 *
 * @example
 * ```typescript
 * const naver = await loadNaverMaps(clientId);
 * const map = new naver.maps.Map(element, { center: new naver.maps.LatLng(37, 127) });
 * ```
 */
export const loadNaverMaps = (
  clientId: string,
  submodules: string = "",
  lang: string = "ko"
): Promise<any> => {
  // Return existing naver object if already loaded
  if (isLoaded && window.naver && window.naver.maps) {
    return Promise.resolve(window.naver);
  }

  // Return existing promise if loading is in progress
  if (isLoading && loadPromise) {
    return loadPromise;
  }

  // Validate client ID
  if (!clientId) {
    return Promise.reject(new Error("Naver Maps Client ID is required"));
  }

  isLoading = true;

  loadPromise = new Promise((resolve, reject) => {
    try {
      // Check if script already exists (remove old script)
      const existingScript = document.querySelector(
        'script[src*="oapi.map.naver.com"]'
      );
      if (existingScript) {
        existingScript.remove();
      }

      // Create script element
      const script = document.createElement("script");
      script.type = "text/javascript";
      script.async = true;

      // Build script URL with parameters (New API with callback)
      const submodulesParam = submodules ? `&submodules=${submodules}` : "";
      const langParam = lang ? `&language=${lang}` : "";
      script.src = `https://oapi.map.naver.com/openapi/v3/maps.js?ncpKeyId=${clientId}${submodulesParam}${langParam}&callback=naverMapsCallback`;

      // Set timeout for loading (30 seconds)
      const timeoutId = setTimeout(() => {
        if (!isLoaded) {
          isLoading = false;
          loadPromise = null;
          delete (window as any).naverMapsCallback;
          reject(new Error("Naver Maps SDK loading timeout (30 seconds)"));
        }
      }, 30000);

      // Define global callback function (Naver Maps official recommendation)
      (window as any).naverMapsCallback = () => {
        clearTimeout(timeoutId);

        // Wait for naver.maps to be fully initialized
        // SDK may call callback before maps object is ready
        const checkInterval = setInterval(() => {
          if (window.naver && window.naver.maps) {
            clearInterval(checkInterval);
            isLoading = false;
            isLoaded = true;
            delete (window as any).naverMapsCallback;
            resolve(window.naver);
          }
        }, 50);

        // Timeout for polling (3 seconds)
        setTimeout(() => {
          clearInterval(checkInterval);
          if (!isLoaded) {
            isLoading = false;
            delete (window as any).naverMapsCallback;
            reject(
              new Error(
                "Naver Maps SDK loaded but naver.maps initialization timeout"
              )
            );
          }
        }, 3000);
      };

      // Handle script loading errors
      script.onerror = () => {
        clearTimeout(timeoutId);
        isLoading = false;
        isLoaded = false;
        loadPromise = null;
        delete (window as any).naverMapsCallback;
        reject(new Error("Failed to load Naver Maps SDK"));
      };

      // Append script to document
      document.head.appendChild(script);
    } catch (error) {
      isLoading = false;
      isLoaded = false;
      loadPromise = null;
      reject(error);
    }
  });

  return loadPromise;
};

/**
 * Reset the loader state (useful for testing)
 */
export const resetNaverMapsLoader = (): void => {
  isLoading = false;
  isLoaded = false;
  loadPromise = null;
};
